import { FC } from "react"
import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer"

import pdfstyles, { footerStyle, ulab_hrz_logo } from "./styles"
import { Font } from "@react-pdf/renderer"

Font.registerHyphenationCallback((word) => [word])

const CURRENT_YEAR = new Date().getFullYear()

/** Horizontal inset matching page padding; keeps header/content aligned on any page size */
const PAGE_INSET = 33
const PAGE_TOP_PADDING = 72
const PAGE_BOTTOM_PADDING = 80

/** DOM selectors for the summary section (must match PdfContent / form markup) */
const SELECTORS = {
  PDF_SECTION: "#pdf-summary-section",
  SUMMARY_DATA_ROW: "summary-data-row",
  SUMMARY_GROUP_TITLE: "summary-group-title",
  SUMMARY_GROUP_TITLE_TEXT: ".summary-group-title-text",
  SUMMARY_INPUT_LABEL: ".summary-input-label",
  SUMMARY_INPUT_VALUE: ".summary-input-value",
  VALUE_CONTAINER_TAGS: 'p, span, div, [class*="MuiTypography"]',
} as const

const headerRowStyle = {
  flexDirection: "row" as const,
  justifyContent: "space-between" as const,
  alignItems: "center" as const,
  alignSelf: "stretch" as const,
  width: "100%" as const,
}

const styles = StyleSheet.create({
  tableContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginBottom: 8,
  },
  row: {
    flexDirection: "row",
    width: "100%",
    alignItems: "stretch",
  },
  rowLast: {
    borderBottomWidth: 0,
  },
  labelCol: {
    width: "40%",
    padding: 6
  },
  valueCol: {
    width: "60%",
    padding: 6,
  },
  label: {
    fontSize: 10,
    color: "rgba(0, 0, 0, 0.60)",
    fontFamily: "Helvetica",
  },
  value: {
    fontSize: 10,
    color: "#000",
    fontFamily: "Helvetica",
    lineHeight: 1.2,
  },
  valueBold: {
    fontSize: 10,
    color: "#000",
    fontFamily: "Helvetica-Bold",
    lineHeight: 1.2,
  },
  groupHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgb(238, 238, 238)",
    padding: 8,
    marginBottom: 4,
    width: "100%",
  },
  groupTitle: {
    fontSize: 8,
    fontWeight: 500,
    color: "rgba(0, 0, 0, 0.6)",
    fontFamily: "Helvetica",
    textTransform: "uppercase",
    letterSpacing: 0.17,
  },
})

export interface ExtractedRow {
  label: string
  value: string
  isBold: boolean
  isGroupTitle?: boolean
}

/** Section = one optional group title + its following data rows (kept together for page breaks) */
export interface PdfSection {
  groupTitle: ExtractedRow | null
  rows: ExtractedRow[]
}

/**
 * Keep regular rows on a single page so label/value stay aligned.
 * Very long values are allowed to wrap/split across pages.
 */
function shouldKeepRowTogether(row: ExtractedRow): boolean {
  const normalizedValueLength = row.value.replace(/\s+/g, " ").trim().length
  const normalizedLabelLength = row.label.replace(/\s+/g, " ").trim().length
  return normalizedValueLength <= 180 && normalizedLabelLength <= 60
}

/**
 * Groups extracted rows so each group title stays with its following form rows.
 * If a title would be orphaned at page end, the whole section moves to the next page.
 */
function groupRowsIntoSections(rows: ExtractedRow[]): PdfSection[] {
  const sections: PdfSection[] = []
  let current: PdfSection = { groupTitle: null, rows: [] }

  for (const row of rows) {
    if (row.isGroupTitle) {
      if (current.groupTitle !== null || current.rows.length > 0) {
        sections.push(current)
        current = { groupTitle: null, rows: [] }
      }
      current.groupTitle = row
    } else {
      current.rows.push(row)
    }
  }
  if (current.groupTitle !== null || current.rows.length > 0) {
    sections.push(current)
  }
  return sections
}

/** Safely get text from an element (textContent, innerText, or walk text nodes). */
function getElementText(el: HTMLElement | null): string {
  if (!el) return ""
  const primary = (el.textContent ?? "").trim()
  if (primary) return primary
  const textNodes: string[] = []
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null)
  let n: Node | null
  while ((n = walker.nextNode())) {
    const t = n.textContent?.trim()
    if (t) textNodes.push(t)
  }
  return textNodes.join("") || (el.innerText ?? "").trim()
}

/** Detect bold from computed or inline style (safe when element is detached or in another document). */
function isElementBold(el: HTMLElement | null): boolean {
  if (!el) return false
  try {
    const fw = window.getComputedStyle(el).fontWeight
    if (fw === "700" || fw === "bold" || Number.parseInt(String(fw), 10) >= 700) return true
    const iw = el.style?.fontWeight
    return iw === "bold" || iw === "700"
  } catch {
    return false
  }
}

/**
 * Extracts form data from JsonFormBuilder's rendered DOM.
 * Processes elements in document order so section titles (e.g. "REVIEW PRESCRIPTION") appear correctly.
 */
export const extractFormDataFromDOM = (container: HTMLElement | null): ExtractedRow[] => {
  const rows: ExtractedRow[] = []
  if (!container) return rows

  const section = container.querySelector(SELECTORS.PDF_SECTION)
  if (!section) return rows

  const walker = document.createTreeWalker(
    section,
    NodeFilter.SHOW_ELEMENT,
    {
      acceptNode: (node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return NodeFilter.FILTER_SKIP
        const el = node as Element
        if (el.classList.contains(SELECTORS.SUMMARY_DATA_ROW)) return NodeFilter.FILTER_ACCEPT
        if (el.classList.contains(SELECTORS.SUMMARY_GROUP_TITLE)) return NodeFilter.FILTER_ACCEPT
        return NodeFilter.FILTER_SKIP
      },
    }
  )

  let node: Node | null
  while ((node = walker.nextNode())) {
    const element = node as Element

    if (element.classList.contains(SELECTORS.SUMMARY_GROUP_TITLE)) {
      const captionText = element.querySelector(SELECTORS.SUMMARY_GROUP_TITLE_TEXT)
      if (captionText) {
        const text = getElementText(captionText as HTMLElement)
        if (text) {
          rows.push({ label: "", value: text, isBold: true, isGroupTitle: true })
        }
      }
    }

    if (element.classList.contains(SELECTORS.SUMMARY_DATA_ROW)) {
      const labelEl = element.querySelector(SELECTORS.SUMMARY_INPUT_LABEL) as HTMLElement | null
      const firstChild = element.firstElementChild as HTMLElement | null
      const label = (
        getElementText(labelEl) ||
        getElementText(firstChild) ||
        ""
      ).trim()

      let valueElement = element.querySelector(SELECTORS.SUMMARY_INPUT_VALUE) as HTMLElement | null
      if (!valueElement) {
        const valueContainer = element.lastElementChild as HTMLElement | null
        if (valueContainer) {
          const candidates = valueContainer.querySelectorAll<HTMLElement>(SELECTORS.VALUE_CONTAINER_TAGS)
          for (let i = 0; i < candidates.length; i++) {
            const el = candidates[i]
            const hasDirectText = Array.from(el.childNodes).some(
              (n) => n.nodeType === Node.TEXT_NODE && (n.textContent?.trim() ?? "")
            )
            if (hasDirectText || el.innerText) {
              valueElement = el
            }
          }
          if (!valueElement) valueElement = valueContainer
        }
      }

      let value = getElementText(valueElement).trim()
      const cleanedValue = value
        .replace(/\r\n|\r/g, "\n")
        .replace(/[ \t]+/g, " ")
        .replace(/\n+/g, "\n")
        .trim()

      const isBold = isElementBold(valueElement)

      if (label || cleanedValue) {
        rows.push({
          label,
          value: cleanedValue,
          isBold,
          isGroupTitle: false,
        })
      }
    }
  }

  return rows
}

type PageSize = "A4" | "LETTER" | "LEGAL"

interface DOMToPDFComponentProps {
  extractedRows: ExtractedRow[]
  title?: string | JSX.Element
  patientId?: string
  patientName?: string
  caseId?: string
  creationDate?: string
  orgName?: string
  /** Page size; layout is responsive to any size */
  pageSize?: PageSize
  /** Dental chart image (data URL or URL); when set, adds an extra page with header/footer */
  dentalChartImage?: string
}

const FooterElements = () => (
  <>
    <Text style={footerStyle} render={({ pageNumber }) => `Page ${pageNumber}`} fixed />
    <Text style={{ ...pdfstyles.footerTextBase, bottom: 10, left: 185 }} fixed>Ω</Text>
    <Text
      style={{ ...pdfstyles.footerTextBase, bottom: 7.5, left: 2, right: 0 }}
      render={() => `\u00A9   ${CURRENT_YEAR} Ulab Systems, Inc. All Rights Reserved`}
      fixed
    />
  </>
)

/** Format optional pair for header (avoids "undefined" in PDF). */
function formatHeaderPair(a: string | undefined, b: string | undefined): string {
  const x = (a ?? "").trim()
  const y = (b ?? "").trim()
  if (!x && !y) return "—"
  return x && y ? `${x} - ${y}` : x || y
}

const HeaderView: FC<{
  patientId?: string
  patientName?: string
  caseId?: string
  creationDate?: string
  title?: string | JSX.Element
}> = ({ patientId, patientName, caseId, creationDate, title }) => (
  <View
    fixed
    style={{
      position: "absolute",
      top: 16,
      left: PAGE_INSET,
      right: PAGE_INSET,
      flexDirection: "column",
    }}
  >
    <View style={headerRowStyle}>
      <Text style={{ fontSize: 9, color: "#666" }}>{formatHeaderPair(patientId, patientName)}</Text>
      <Text style={{ fontSize: 9, color: "#666" }}>{formatHeaderPair(caseId, creationDate)}</Text>
    </View>
    <View style={{ ...headerRowStyle, marginTop: 2 }}>
      <Text style={{ ...pdfstyles.text, fontSize: 14, flex: 1, minWidth: 0 }}>
        {typeof title === "string" ? title : title ?? ""}
      </Text>
      <View style={pdfstyles.logocontainer}>
        <Image style={{ ...pdfstyles.logo, width: 88, height: 40, flexShrink: 0 }} source={ulab_hrz_logo} />
      </View>
    </View>
  </View>
)

export const DOMToPDFComponent: FC<DOMToPDFComponentProps> = ({
  extractedRows,
  title,
  patientId,
  patientName,
  caseId,
  creationDate,
  orgName,
  pageSize = "A4",
  dentalChartImage,
}) => {
  const pageStyle = {
    ...pdfstyles.page,
    paddingTop: PAGE_TOP_PADDING,
    paddingBottom: PAGE_BOTTOM_PADDING,
  }

  return (
    <Document>
      <Page style={pageStyle} size={pageSize} wrap>
        <HeaderView patientId={patientId} patientName={patientName} caseId={caseId} creationDate={creationDate} title={title} />

        {/* Content: full width with same horizontal inset as header */}
        <View style={{ ...pdfstyles.content, paddingTop: 0, paddingLeft: PAGE_INSET, paddingRight: PAGE_INSET, width: "100%", flex: 1 }}>
          {/* First Page Summary Info */}
          <View style={{ marginTop: 2, marginBottom: 8 }}>
            <View style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <Text style={{ ...pdfstyles.text, fontSize: 10, fontWeight: 400, lineHeight: "100%" }}>Company:</Text>
              <Text style={{ ...pdfstyles.text, fontSize: 10, fontWeight: 700, lineHeight: "100%" }}>{orgName ?? "—"}</Text>
            </View>
            <View style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <Text style={{ ...pdfstyles.text, fontSize: 10, fontWeight: 400, lineHeight: "100%" }}>Patient name:</Text>
              <Text style={{ ...pdfstyles.text, fontSize: 10, fontWeight: 700, lineHeight: "100%" }}>{patientName ?? "—"}</Text>
            </View>
            <View style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <Text style={{ ...pdfstyles.text, fontSize: 10, fontWeight: 400, lineHeight: "100%" }}>Case ID:</Text>
              <Text style={{ ...pdfstyles.text, fontSize: 10, fontWeight: 400, lineHeight: "100%" }}>{caseId ?? "—"}</Text>
            </View>
            <View style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <Text style={{ ...pdfstyles.text, fontSize: 10, fontWeight: 400, lineHeight: "100%" }}>uAssist submission date:</Text>
              <Text style={{ ...pdfstyles.text, fontSize: 10, fontWeight: 400 }}>{creationDate ?? "—"}</Text>
            </View>
          </View>

          {/* Form rows: keep rows fully breakable so long values can flow across pages */}
          {groupRowsIntoSections(extractedRows ?? []).map((sec, sectionIndex) => (
            <View key={`section-${sectionIndex}`} wrap style={{ width: "100%" }}>
              {sec.groupTitle ? (
                <View style={styles.groupHeader} wrap={false}>
                  <Text style={styles.groupTitle}>{sec.groupTitle.value}</Text>
                </View>
              ) : null}
              {sec.rows.length > 0 && (
                <View style={styles.tableContainer}>
                  {sec.rows.map((row, rowIndex) => {
                    const isLastRow = rowIndex === sec.rows.length - 1
                    const keepRowTogether = shouldKeepRowTogether(row)
                    return (
                      <View
                        key={`row-${sectionIndex}-${rowIndex}`}
                        wrap={!keepRowTogether}
                        style={[styles.row, isLastRow ? styles.rowLast : {}]}
                      >
                        <View style={styles.labelCol}>
                          <Text style={styles.label}>{row.label}</Text>
                        </View>
                        <View style={styles.valueCol}>
                          <Text style={row.isBold ? styles.valueBold : styles.value}>{row.value}</Text>
                        </View>
                      </View>
                    )
                  })}
                </View>
              )}
            </View>
          ))}
        </View>

        <FooterElements />
      </Page>

      {/* Dental chart page when BE returns image (e.g. dental_chart.png); same header/footer */}
      {dentalChartImage && (
        <Page style={pageStyle} size={pageSize}>
          <HeaderView patientId={patientId} patientName={patientName} caseId={caseId} creationDate={creationDate} title={title} />
          <View style={{ ...pdfstyles.content, paddingLeft: PAGE_INSET, paddingRight: PAGE_INSET }}>
            <Image source={dentalChartImage} />
          </View>
          <FooterElements />
        </Page>
      )}
    </Document>
  )
}
